import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Megaphone, Zap, ArrowRight, TrendingUp, Eye, Target, Plus, Activity, PoundSterling, Users, Rocket, Repeat, Send } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { HoloCard } from '@/components/HoloCard';
import { NoData } from '@/components/NoData';
import { XAxis, YAxis, ResponsiveContainer, Tooltip, Area, AreaChart, CartesianGrid } from 'recharts';

function Kpi({ label, value, delta, accent, icon: I }) {
  return (
    <HoloCard className="glass holo-edge glass-hover rounded-2xl p-5" data-testid={`kpi-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">{label}</div>
        <I className={`w-4 h-4 ${accent}`}/>
      </div>
      <div className="font-display font-black text-3xl">{value}</div>
      {delta != null && <div className="text-xs text-boosta-emerald mt-1">{delta}</div>}
    </HoloCard>
  );
}

function OnboardingChecklist() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('boosta_onboarding_done') === '1');
  useEffect(() => {
    if (!dismissed) api.get('/onboarding/status').then(r => setStatus(r.data)).catch(() => {});
  }, [dismissed]);
  if (dismissed || !status) return null;
  const steps = [
    { done: status.meta_connected, label: 'Connect your Meta account', hint: 'Link Facebook & Instagram in Business Hub', to: '/business-hub', testid: 'ob-meta' },
    { done: status.has_creative, label: 'Generate your first AI creative', hint: 'An ad image, video or copy in Creative Studio', to: '/creatives', testid: 'ob-creative' },
    { done: status.has_post, label: 'Publish or schedule a post', hint: 'Push a creative to your pages from Publisher', to: '/composer', testid: 'ob-post' },
    { done: status.has_campaign, label: 'Launch your first campaign', hint: 'Brief it, get AI copy, push it to Meta', to: '/campaigns/new', testid: 'ob-campaign' },
  ];
  const doneCount = steps.filter(s => s.done).length;
  if (doneCount === steps.length) { localStorage.setItem('boosta_onboarding_done', '1'); return null; }
  return (
    <div className="glass rounded-2xl p-6 border border-boosta-cyan/20" data-testid="onboarding-checklist">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-1">Getting started · {doneCount}/{steps.length}</div>
          <div className="font-display font-bold text-xl">Your first winning ad in 4 steps</div>
        </div>
        <button onClick={() => { localStorage.setItem('boosta_onboarding_done', '1'); setDismissed(true); }}
          className="text-xs text-white/40 hover:text-white" data-testid="ob-dismiss">Dismiss</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {steps.map((s, i) => (
          <Link key={s.label} to={s.to} data-testid={s.testid}
            className={`rounded-xl p-4 border transition-colors ${s.done ? 'border-boosta-emerald/30 bg-boosta-emerald/[0.06]' : 'border-white/10 bg-black/25 hover:border-boosta-cyan/40'}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${s.done ? 'bg-boosta-emerald text-boosta-ink' : 'bg-white/10 text-white/60'}`}>
                {s.done ? '✓' : i + 1}
              </span>
              <span className={`text-sm font-semibold ${s.done ? 'text-white/50 line-through' : 'text-white'}`}>{s.label}</span>
            </div>
            <div className="text-xs text-white/45 leading-relaxed">{s.hint}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function RevenueCard({ rev }) {
  const [eligible, setEligible] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    api.get('/admin/winback/preview').then(r => setEligible(r.data.eligible)).catch(() => {});
  }, []);

  const sendWinback = async () => {
    if (!window.confirm(`Send the "trials are live" win-back email to ${eligible} users? Each user only ever receives it once.`)) return;
    setSending(true);
    try {
      const { data } = await api.post('/admin/winback');
      toast.success(`Queued ${data.queued} win-back emails — sending in the background.`);
      setEligible(0);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSending(false);
  };

  if (!rev) return null;
  const tiles = [
    { label: 'Active subs', value: rev.active_subscribers, icon: Repeat, accent: 'text-boosta-cyan', testid: 'rev-active-subs' },
    { label: 'Active trials', value: rev.active_trials, icon: Rocket, accent: 'text-boosta-yellow', testid: 'rev-active-trials' },
    { label: 'Conversions', value: `${rev.conversions} · ${rev.conversion_rate}%`, icon: Target, accent: 'text-boosta-emerald', testid: 'rev-conversions' },
    { label: 'Collected', value: `£${rev.collected_revenue.toLocaleString()}`, icon: PoundSterling, accent: 'text-white', testid: 'rev-collected' },
    { label: 'Users · 7d new', value: `${rev.total_users} · +${rev.signups_7d}`, icon: Users, accent: 'text-boosta-cyan', testid: 'rev-users' },
  ];
  return (
    <div className="glass rounded-2xl p-6 border border-boosta-yellow/25 relative overflow-hidden" data-testid="revenue-dashboard">
      <div className="absolute -inset-10 bg-gradient-to-br from-boosta-yellow/10 via-transparent to-boosta-cyan/10 blur-3xl pointer-events-none" />
      <div className="relative flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-8">
        <div className="min-w-[180px]">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-yellow mb-1">Revenue · Admin only</div>
          <div className="font-display font-black text-5xl text-3d-yellow" data-testid="rev-mrr">£{rev.mrr.toLocaleString()}</div>
          <div className="text-xs text-white/45 mt-1">Live MRR from {rev.active_subscribers} active subscription{rev.active_subscribers === 1 ? '' : 's'}</div>
        </div>
        <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 w-full">
          {tiles.map((t) => (
            <div key={t.label} className="rounded-xl bg-black/30 border border-white/5 p-3.5" data-testid={t.testid}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[9px] tracking-[0.15em] uppercase font-bold text-white/40">{t.label}</span>
                <t.icon className={`w-3.5 h-3.5 ${t.accent}`} />
              </div>
              <div className="font-display font-bold text-lg leading-none">{t.value}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="relative mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 items-end">
        <div className="lg:col-span-2">
          <div className="text-[9px] tracking-[0.2em] uppercase font-bold text-white/40 mb-2">Revenue collected · last 6 months</div>
          <div className="h-28" data-testid="rev-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={rev.trend || []} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="grev" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#E5FF00" stopOpacity="0.55"/>
                    <stop offset="100%" stopColor="#E5FF00" stopOpacity="0"/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" stroke="rgba(255,255,255,0.3)" fontSize={10}/>
                <YAxis stroke="rgba(255,255,255,0.3)" fontSize={10}/>
                <Tooltip contentStyle={{ background: 'rgba(8,10,18,0.95)', border: '1px solid rgba(229,255,0,0.3)', borderRadius: 12 }} formatter={(v) => [`£${v}`, 'Revenue']}/>
                <Area type="monotone" dataKey="revenue" stroke="#E5FF00" fill="url(#grev)" strokeWidth={2}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl bg-black/30 border border-white/5 p-4">
          <div className="text-[9px] tracking-[0.2em] uppercase font-bold text-white/40 mb-1.5">Win-back campaign</div>
          <div className="text-xs text-white/55 leading-relaxed mb-3">
            {eligible === null ? 'Checking eligible users…'
              : eligible === 0 ? 'No eligible users right now — everyone has been contacted or is already subscribed.'
              : <><span className="text-white font-bold">{eligible}</span> free users haven't heard that trials are live.</>}
          </div>
          <button onClick={sendWinback} disabled={sending || !eligible}
            className="w-full btn-primary rounded-xl py-2.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40"
            data-testid="winback-send-btn">
            <Send className="w-3.5 h-3.5"/> {sending ? 'Queuing…' : 'Send win-back email'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([api.get('/analytics/summary'), api.get('/campaigns')]);
        setSummary(s.data); setCampaigns(c.data);
      } catch (err) { console.error('Dashboard load failed', err); }
      if (isAdmin) {
        try { const { data } = await api.get('/admin/revenue'); setRevenue(data); }
        catch (err) { console.error('Revenue load failed', err); }
      }
    })();
  }, [isAdmin]);

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-2">Command Centre</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Hello, {user?.name?.split(' ')[0] || 'Operator'}.</h1>
          <p className="text-white/50 text-sm mt-1">{summary?.has_data ? "Here's how your campaigns are performing right now." : "Let's get your first campaign live — start with the checklist below."}</p>
        </div>
        <div className="flex gap-3">
          <Link to="/creatives" className="btn-ghost rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2"><Sparkles className="w-4 h-4"/> Generate creative</Link>
          <Link to="/campaigns/new" className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4"/> New campaign</Link>
        </div>
      </div>

      {isAdmin && <RevenueCard rev={revenue} />}

      <OnboardingChecklist />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Spend" value={`£${(summary?.total_spend ?? 0).toLocaleString()}`} accent="text-white" icon={Activity}/>
        <Kpi label="Impressions" value={(summary?.total_impressions ?? 0).toLocaleString()} accent="text-boosta-cyan" icon={Eye}/>
        <Kpi label="Conversions" value={(summary?.total_conversions ?? 0).toLocaleString()} accent="text-boosta-yellow" icon={Target}/>
        <Kpi label="ROI" value={`${summary?.roi ?? 0}%`} accent="text-boosta-emerald" icon={TrendingUp}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 glass rounded-2xl p-6">
          <div className="flex justify-between items-center mb-5">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan">Performance · last 7 days</div>
              <div className="font-display font-bold text-xl mt-1">Spend & conversions</div>
            </div>
          </div>
          <div className="h-64">
            {summary?.has_data && summary?.trend?.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={summary?.trend || []}>
                <defs>
                  <linearGradient id="gspend" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.55"/>
                    <stop offset="100%" stopColor="#00F0FF" stopOpacity="0"/>
                  </linearGradient>
                  <linearGradient id="gconv" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#E5FF00" stopOpacity="0.5"/>
                    <stop offset="100%" stopColor="#E5FF00" stopOpacity="0"/>
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3"/>
                <XAxis dataKey="day" stroke="rgba(255,255,255,0.3)" fontSize={11}/>
                <YAxis stroke="rgba(255,255,255,0.3)" fontSize={11}/>
                <Tooltip contentStyle={{ background: 'rgba(8,10,18,0.95)', border: '1px solid rgba(0,240,255,0.3)', borderRadius: 12 }} />
                <Area type="monotone" dataKey="spend" stroke="#00F0FF" fill="url(#gspend)" strokeWidth={2}/>
                <Area type="monotone" dataKey="conversions" stroke="#E5FF00" fill="url(#gconv)" strokeWidth={2}/>
              </AreaChart>
            </ResponsiveContainer>
            ) : (
              <NoData title="No performance data yet"
                hint="Numbers here are always real — they appear once your campaigns run on a connected, ads-approved Meta account. Nothing is simulated." />
            )}
          </div>
        </div>
        <div className="glass rounded-2xl p-6">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-yellow mb-2">ZAP Balance</div>
          <div className="font-display font-black text-5xl gradient-text" data-testid="dash-zap-balance">{(user?.zap_credits ?? 0).toLocaleString()}</div>
          <div className="text-xs text-white/40 mt-2 mb-5">Credits available · plan: {user?.plan}</div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-white/55">Image ad (original)</span><span className="text-white/80">25 ZAP</span></div>
            <div className="flex justify-between"><span className="text-white/55">Image ad (regenerate)</span><span className="text-white/80">15 ZAP</span></div>
            <div className="flex justify-between"><span className="text-white/55">Video ad · Pro</span><span className="text-white/80">100 ZAP</span></div>
            <div className="flex justify-between"><span className="text-white/55">Ad copy batch</span><span className="text-white/80">5 ZAP</span></div>
            <div className="flex justify-between"><span className="text-white/55">Campaign launch</span><span className="text-white/80">50 ZAP</span></div>
          </div>
          <Link to="/payments" className="mt-6 w-full btn-primary rounded-xl py-3 text-sm inline-flex items-center justify-center gap-2" data-testid="dash-topup-zap">
            <Zap className="w-4 h-4"/> Top up ZAP
          </Link>
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="flex justify-between items-center mb-5">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-emerald">Active campaigns</div>
            <div className="font-display font-bold text-xl mt-1">{campaigns.length} total · {summary?.active ?? 0} live</div>
          </div>
          <Link to="/campaigns" className="text-sm text-boosta-cyan inline-flex items-center gap-1 hover:underline">View all <ArrowRight className="w-3 h-3"/></Link>
        </div>
        {campaigns.length === 0 ? (
          <div className="text-center py-12 text-white/45">
            <Megaphone className="w-10 h-10 mx-auto mb-3 text-white/20"/>
            <div className="text-sm">No campaigns yet — launch your first one.</div>
            <Link to="/campaigns/new" className="mt-4 btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4"/> Create campaign</Link>
          </div>
        ) : (
          <div className="space-y-2">
            {campaigns.slice(0, 5).map(c => (
              <div key={c.campaign_id} className="flex justify-between items-center py-3 px-4 rounded-xl border border-white/5 hover:border-boosta-cyan/30 transition" data-testid={`dash-campaign-${c.campaign_id}`}>
                <div className="flex items-center gap-4">
                  <span className={`w-2 h-2 rounded-full ${c.status==='live'?'bg-boosta-emerald':'bg-white/30'} ${c.status==='live'?'pulse-glow':''}`}/>
                  <div>
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-white/40 uppercase tracking-wider">{c.platform} · {c.status}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold">£{(c.spend ?? 0).toFixed(2)}</div>
                  <div className="text-xs text-white/45">{c.conversions ?? 0} conv · {c.roas ?? 0}x ROAS</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
